import { create } from 'zustand';
import axios from 'axios';
import { API_integrate_toast } from '../constants';
import { getDefaultAxiosConfig } from '../main/content/requests/utilities/requests';

const useIntegratePOSToastStore = create((set, get) => ({
  // Toast-specific form data - updated for API integration
  formData: {
    // Prerequisites (Step 0)
    toastUserCreated: false,

    // API Credentials Creation (Step 1)
    apiCredentialsCreated: false,

    // API Credential Values (Step 2)
    clientId: '',
    clientSecret: '',

  },

  // Current step/page
  currentStep: 0,

  // Total steps for Toast API integration
  totalSteps: 3, // Information, API Credentials Creation, Credential Input

  // Actions
  setFormData: (field, value) => set((state) => ({
    formData: {
      ...state.formData,
      [field]: value,
    },
  })),

  // Step 0: Prerequisites validation
  setToastUserCreated: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      toastUserCreated: completed,
    },
  })),

  // Step 1: API credentials creation validation
  setAPICredentialsCreated: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      apiCredentialsCreated: completed,
    },
  })),

  // Legacy setters - keeping for backward compatibility
  // Remove these if not needed elsewhere in your app
  setSSHKeyAdded: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      sshKeyAdded: completed,
    },
  })),

  setDataExportsConfigured: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      dataExportsConfigured: completed,
    },
  })),

  // Navigation
  nextStep: () => set((state) => ({
    currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1),
  })),

  previousStep: () => set((state) => ({
    currentStep: Math.max(state.currentStep - 1, 0),
  })),

  resetForm: () => set(() => ({
    formData: {
      toastUserCreated: false,
      apiCredentialsCreated: false,
      clientId: '',
      clientSecret: '',
    },
    currentStep: 0,
  })),

  // Updated validation helpers for API integration
  isStepValid: (step) => {
    const { formData } = get();
    switch (step) {
      case 0: // Information page - Prerequisites check
        return formData.toastUserCreated;
      case 1: // API credentials creation
        return formData.apiCredentialsCreated;
      case 2: // Credential input
        return (
          formData.clientId.trim().length > 0 &&
          formData.clientSecret.trim().length > 0
        );
      default:
        return false;
    }
  },

  // Toast integration function
  integrateToast: async (company_id) => {
    try {
      const { formData, resetForm } = get();

      // Get axios config with proper authentication
      const axiosConfig = await getDefaultAxiosConfig();

      // Make API call to your backend to save the Toast credentials
      const response = await axios.post(API_integrate_toast, {
        company_id,
        integrationType: 'api',
        client_id: formData.clientId,
        client_secret: formData.clientSecret,
        createdAt: new Date().toISOString(),
        version: '1.0'
      }, axiosConfig);

      const connectionSuccessful = Boolean(
        response?.data?.redux?.payload?.connection_successful
      );

      if (!connectionSuccessful) {
        return {
          success: false,
          error: "POS authentication failed. Please verify your credentials and try again.",
        };
      }

      // Reset the form after successful integration
      resetForm();

      return {
        success: true,
        data: {
          pos: 'toast',
          integrationType: 'api'
        }
      };

    } catch (error) {
      const serverMessage = error?.response?.data?.system?.message;

      return {
        success: false,
        error: serverMessage || 'Failed to integrate Toast. Please try again.'
      };
    }
  },
}));

export default useIntegratePOSToastStore;
